/**
 * Cart Drawer v2.1 — open/close + AJAX cart operations.
 *
 * Fixes:
 *  - refreshDrawer() replaces entire panel (items + footer + discounts)
 *  - Global mutex prevents all overlapping cart requests
 *  - .catch() on every Promise chain — drawer never gets stuck
 *  - HTTP status checked on every fetch — errors handled gracefully
 *  - Back/forward navigation syncs cart count (bfcache + visibilitychange)
 *  - Empty cart state handled correctly
 *  - Fetch timeout prevents permanent mutex lock
 */
(function () {
  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  /* ------- State ------- */
  var busy = false;
  var lastSyncTime = 0;
  var SYNC_DEBOUNCE = 5000; /* ms — don't re-sync more often than this */
  var FETCH_TIMEOUT = 10000; /* ms — abort fetch after this */

  /* ------- Fetch helper with timeout + status check ------- */

  function fetchWithTimeout(url, options) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT);

    var opts = options || {};
    opts.signal = controller.signal;

    return fetch(url, opts)
      .then(function (r) {
        clearTimeout(timer);
        if (!r.ok) throw new Error('Cart API error: ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  }

  /* ------- Helpers ------- */

  function open() {
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function isOpen() {
    return drawer.classList.contains('is-open');
  }

  function unlock() {
    busy = false;
  }

  /**
   * Re-render the cart drawer via Section Rendering API.
   * Replaces items, subtotal, discounts AND footer.
   */
  function refreshDrawer() {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT);

    return fetch('/?sections=cart-drawer', { signal: controller.signal })
      .then(function (r) {
        clearTimeout(timer);
        if (!r.ok) throw new Error('Section API error: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;

        var tmp = document.createElement('div');
        tmp.innerHTML = html;

        /* --- Replace cart items --- */
        var newItems = tmp.querySelector('[data-cart-items]');
        var currentItems = drawer.querySelector('[data-cart-items]');
        if (currentItems) {
          currentItems.innerHTML = newItems ? newItems.innerHTML : '';
        }

        /* --- Replace subtotal --- */
        var newSubtotal = tmp.querySelector('[data-cart-subtotal]');
        var currentSubtotal = drawer.querySelector('[data-cart-subtotal]');
        if (newSubtotal && currentSubtotal) {
          currentSubtotal.textContent = newSubtotal.textContent;
        }

        /* --- Replace discounts --- */
        var newDiscounts = tmp.querySelector('[data-cart-discounts]');
        var currentDiscounts = drawer.querySelector('[data-cart-discounts]');
        if (newDiscounts && currentDiscounts) {
          currentDiscounts.innerHTML = newDiscounts.innerHTML;
        }

        /* --- Replace footer (checkout button + min-order message) --- */
        var newFooter = tmp.querySelector('.cart-drawer__footer');
        var currentFooter = drawer.querySelector('.cart-drawer__footer');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        }
      })
      .catch(function (err) {
        clearTimeout(timer);
        console.error('Drawer refresh error:', err);
      });
  }

  /**
   * Update cart count badge in header.
   */
  function updateCartCount(count, pulse) {
    var badges = document.querySelectorAll('[data-cart-count]');
    badges.forEach(function (badge) {
      badge.textContent = count;
      badge.setAttribute('data-cart-count', count);
    });

    if (pulse) {
      triggerPulse();
    }
  }

  function triggerPulse() {
    var cartBtn = document.querySelector('[data-cart-toggle]');
    if (cartBtn) {
      cartBtn.classList.remove('is-pulse');
      void cartBtn.offsetWidth;
      cartBtn.classList.add('is-pulse');
    }
  }

  /**
   * Fetch current cart and update badge. Debounced.
   */
  function syncCartCount() {
    var now = Date.now();
    if (now - lastSyncTime < SYNC_DEBOUNCE) {
      return Promise.resolve();
    }
    lastSyncTime = now;

    return fetchWithTimeout('/cart.js')
      .then(function (cart) {
        updateCartCount(cart.item_count);
        return cart;
      })
      .catch(function () { /* silent — badge stays as-is */ });
  }

  /* ------- Open / Close ------- */

  document.addEventListener('click', function (e) {
    /* Toggle */
    var toggleBtn = e.target.closest('[data-cart-toggle]');
    if (toggleBtn) {
      e.preventDefault();
      if (isOpen()) {
        close();
      } else {
        if (busy) return; /* don't re-open while request is in flight */
        busy = true;
        refreshDrawer()
          .then(function () { return syncCartCount(); })
          .then(open)
          .catch(function () { open(); }) /* always open, even on error */
          .finally(unlock);
      }
      return;
    }

    /* Close button (X) */
    var closeBtn = e.target.closest('[data-cart-close]');
    if (closeBtn && isOpen()) {
      close();
      return;
    }

    /* Overlay click */
    if (e.target === drawer && isOpen()) {
      close();
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) close();
  });

  /* ------- Keep cart badge in sync ------- */

  /**
   * Force-sync badge: bypasses debounce, fetches /cart.js and updates all badges.
   * Used on page load and navigation events where the badge MUST be correct.
   */
  function forceSyncBadge() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCartCount(cart.item_count); })
      .catch(function () { /* silent */ });
  }

  /* Always force-sync on script init — Shopify HTML cache may have stale count */
  forceSyncBadge();

  /* Sync on every pageshow (covers bfcache, back/forward, and normal navigation) */
  window.addEventListener('pageshow', function () {
    forceSyncBadge();
  });

  /* Sync when tab becomes visible again */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) syncCartCount();
  });

  /* ------- AJAX: Add to Cart ------- */

  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest('[data-product-add]');
    if (!addBtn) return;
    e.preventDefault();

    if (busy) return;

    var variantId = addBtn.getAttribute('data-variant-id')
      || addBtn.getAttribute('data-product-id');
    if (!variantId) return;

    var isProductPage = addBtn.hasAttribute('data-variant-id');

    /* Quantity */
    var wrapper = addBtn.closest('form')
      || addBtn.closest('[data-qty-wrapper]')
      || addBtn.parentElement;
    var qtyInput = wrapper ? wrapper.querySelector('[data-qty-input]') : null;
    if (!qtyInput && addBtn.parentElement) {
      var siblingWrapper = addBtn.parentElement.querySelector('[data-qty-wrapper]');
      if (siblingWrapper) qtyInput = siblingWrapper.querySelector('[data-qty-input]');
    }
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    busy = true;
    addBtn.setAttribute('disabled', '');

    fetchWithTimeout('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty })
    })
      .then(function () {
        return Promise.all([refreshDrawer(), syncCartCount()]);
      })
      .then(function () {
        if (isProductPage) {
          open();
        } else {
          triggerPulse();
        }
      })
      .catch(function (err) {
        console.error('Cart add error:', err);
      })
      .finally(function () {
        addBtn.removeAttribute('disabled');
        unlock();
      });
  });

  /* ------- AJAX: Update Quantity ------- */

  drawer.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (!input || busy) return;

    var item = input.closest('[data-cart-item]');
    if (!item) return;

    var lineKey = item.getAttribute('data-line-key');
    var newQty = parseInt(input.value, 10);
    if (!lineKey || isNaN(newQty) || newQty < 1) return;

    busy = true;

    fetchWithTimeout('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: newQty })
    })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        return refreshDrawer();
      })
      .catch(function (err) {
        console.error('Cart update error:', err);
      })
      .finally(unlock);
  });

  /* ------- AJAX: Remove Item ------- */

  drawer.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (!removeBtn) return;
    e.preventDefault();

    if (busy) return;

    var item = removeBtn.closest('[data-cart-item]');
    if (!item) return;

    var lineKey = item.getAttribute('data-line-key');
    if (!lineKey) return;

    busy = true;
    item.style.opacity = '0.5';
    item.style.pointerEvents = 'none';

    fetchWithTimeout('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        return refreshDrawer();
      })
      .catch(function (err) {
        console.error('Cart remove error:', err);
        /* Restore item visibility on error */
        if (item.parentNode) {
          item.style.opacity = '';
          item.style.pointerEvents = '';
        }
      })
      .finally(unlock);
  });
})();